import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFirebase } from "../../../../context/FirebaseContext";
import { getStoreOrders, updateOrderStatus } from "../../../../firebase/order-functions";
import { DEFAULT_STORE_ID } from "../../../../firebase/firebase";
import { useStoreSettings } from "../../../../hooks/useStoreSettings";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminOrdersTranslation } from "../translations";
import "./AdminOrders.css";

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [error, setError] = useState("");
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();
    const { t } = useAdminOrdersTranslation(language);

    // Get status from URL query params if present
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const statusParam = queryParams.get('status');
        if (statusParam) {
            setStatusFilter(statusParam);
        }
    }, [location.search]);

    useEffect(() => {
        const fetchOrders = async () => {
            // Use DEFAULT_STORE_ID directly
            const activeStoreId = DEFAULT_STORE_ID;

            setLoading(true);
            setError("");

            try {
                // console.log(`Fetching orders for store: ${activeStoreId}`);
                const status = statusFilter !== "All" ? statusFilter : null;

                // Fetch orders from Firebase store collection (not user collection)
                const result = await getStoreOrders(activeStoreId, {
                    limit: 10,
                    status,
                    sortBy: 'createdAt',
                    sortDirection: 'desc'
                });

                // Process orders to ensure payment is a string, not an object
                const processedOrders = result.orders.map(order => ({
                    ...order,
                    // Format payment information properly
                    payment: formatPaymentInfo(order.payment, order)
                }));

                // console.log(`Fetched ${processedOrders.length} orders from store collection`);
                setOrders(processedOrders);
                setLastDoc(result.lastDoc);
                setHasMore(processedOrders.length === 10); // Simple check if there might be more
            } catch (err) {
                console.error("Error fetching orders:", err);
                setError(t('failedToLoadOrders'));
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [statusFilter]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return t('dateNotAvailable');

        let date;
        try {
            // Handle different timestamp formats
            if (dateString.toDate && typeof dateString.toDate === 'function') {
                date = dateString.toDate();
            } else if (dateString instanceof Date) {
                date = dateString;
            } else if (typeof dateString === 'string') {
                date = new Date(dateString);
            } else if (typeof dateString === 'number') {
                date = new Date(dateString);
            } else {
                return t('dateNotAvailable');
            }

            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            console.error("Error formatting date:", e, dateString);
            return t('dateNotAvailable');
        }
    };

    // Helper function to format payment information
    const formatPaymentInfo = (payment, order) => {
        // First check if there's a paymentMethod field (which is the case in your DB structure)
        if (order && order.paymentMethod) {
            // Format paymentMethod field nicely
            switch (order.paymentMethod) {
                case 'cash_on_delivery':
                    return t('cashOnDelivery');
                case 'credit_card':
                    return t('creditCard');
                case 'paypal':
                    return t('paypal');
                case 'apple_pay':
                    return t('applePay');
                default:
                    return order.paymentMethod.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            }
        }

        // If no paymentMethod, fall back to the payment field logic
        // If payment is not an object, return as is (likely a string)
        if (!payment || typeof payment !== 'object') {
            return payment || t('paymentNotAvailable');
        }

        // If payment is an object, format it into a readable string
        if (payment.method) {
            return payment.method;
        } else if (payment.transactionId) {
            return t('transactionNumber', { id: payment.transactionId.substring(0, 8) });
        } else {
            // Convert the payment object to a readable string
            return Object.entries(payment)
                .map(([key, value]) => {
                    // Skip success or other boolean properties
                    if (typeof value === 'boolean') return null;
                    // Format the key and value
                    return `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`;
                })
                .filter(Boolean) // Remove nulls
                .join(', ') || t('paymentNotAvailable');
        }
    };

    // Load more orders
    const loadMoreOrders = async () => {
        const activeStoreId = DEFAULT_STORE_ID;

        if (!lastDoc) return;

        setLoading(true);

        try {
            const status = statusFilter !== "All" ? statusFilter : null;

            const result = await getStoreOrders(activeStoreId, {
                limit: 10,
                lastDoc,
                status,
                sortBy: 'createdAt',
                sortDirection: 'desc'
            });

            // Process orders to ensure payment is a string, not an object
            const processedOrders = result.orders.map(order => ({
                ...order,
                // Format payment information properly
                payment: formatPaymentInfo(order.payment, order)
            }));

            setOrders([...orders, ...processedOrders]);
            setLastDoc(result.lastDoc);
            setHasMore(result.orders.length === 10);
        } catch (err) {
            console.error("Error loading more orders:", err);
            setError(t('failedToLoadMoreOrders'));
        } finally {
            setLoading(false);
        }
    };


    // Filter orders based on search and status
    const filteredOrders = orders.filter(order => {
        const matchesSearch =
            order.id?.toLowerCase().includes(search.toLowerCase()) ||
            order.orderNumber?.toString().includes(search.toLowerCase()) ||
            (order.customer?.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (order.customer?.email || '').toLowerCase().includes(search.toLowerCase()) ||
            // Add search in customerInfo which is the standard field
            (order.customerInfo?.firstName || '').toLowerCase().includes(search.toLowerCase()) ||
            (order.customerInfo?.lastName || '').toLowerCase().includes(search.toLowerCase()) ||
            (order.customerInfo?.email || '').toLowerCase().includes(search.toLowerCase());

        const matchesStatus = statusFilter === "All" || order.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Helper function to get customer name from order data
    const getCustomerName = (order) => {
        // Check different possible structures
        if (order.customer?.name) {
            return order.customer.name;
        } else if (order.customerInfo) {
            // Most common structure after checkout
            return `${order.customerInfo.firstName || ''} ${order.customerInfo.lastName || ''}`.trim();
        } else if (order.billingInfo) {
            // Alternative structure
            return `${order.billingInfo.firstName || ''} ${order.billingInfo.lastName || ''}`.trim();
        } else if (order.shippingInfo) {
            // Another alternative
            return `${order.shippingInfo.firstName || ''} ${order.shippingInfo.lastName || ''}`.trim();
        }
        return t('guest');
    };

    // Helper function to get customer email from order data
    const getCustomerEmail = (order) => {
        // Check different possible structures
        if (order.customer?.email) {
            return order.customer.email;
        } else if (order.customerInfo?.email) {
            return order.customerInfo.email;
        } else if (order.billingInfo?.email) {
            return order.billingInfo.email;
        } else if (order.shippingInfo?.email) {
            return order.shippingInfo.email;
        }
        return t('dateNotAvailable');
    };

    // Handle bulk selection
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedOrders(filteredOrders.map(order => order.id));
        } else {
            setSelectedOrders([]);
        }
    };

    // Handle individual selection
    const handleSelectOrder = (orderId) => {
        if (selectedOrders.includes(orderId)) {
            setSelectedOrders(selectedOrders.filter(id => id !== orderId));
        } else {
            setSelectedOrders([...selectedOrders, orderId]);
        }
    };

    // Fixed handleBulkAction function for AdminOrders.js
    // Replace the existing handleBulkAction with this improved version

    const handleBulkAction = async (action) => {
        if (selectedOrders.length === 0) {
            alert(t('selectAtLeastOneOrder'));
            return;
        }

        const activeStoreId = DEFAULT_STORE_ID;

        setLoading(true);
        setError("");

        try {
            let newStatus = "";
            switch (action) {
                case "mark-processing": newStatus = "Processing"; break;
                case "mark-shipped": newStatus = "Shipped"; break;
                case "mark-delivered": newStatus = "Delivered"; break;
                default:
                    setError(t('invalidActionSelected'));
                    setLoading(false);
                    return;
            }

            // console.log(`Using store ID for operations: ${activeStoreId}`);

            // First check permissions once before attempting multiple updates
            const checkPermission = await updateOrderStatus(activeStoreId, selectedOrders[0], newStatus);

            if (!checkPermission.success) {
                setError(t('permissionCheckFailed', { message: checkPermission.message }));
                setLoading(false);
                return;
            }

            let successCount = 0;
            let failedOrders = [];

            // Update each order one by one
            for (const orderId of selectedOrders) {
                try {
                    // console.log(`Updating order ${orderId} to ${newStatus}`);
                    const result = await updateOrderStatus(activeStoreId, orderId, newStatus);

                    if (result.success) {
                        successCount++;
                    } else {
                        failedOrders.push({ id: orderId, message: result.message });
                    }
                } catch (orderError) {
                    console.error(`Error updating order ${orderId}:`, orderError);
                    failedOrders.push({ id: orderId, message: orderError.message });
                }
            }

            // Update local state for all successfully updated orders
            if (successCount > 0) {
                const updatedOrders = orders.map(order =>
                    selectedOrders.includes(order.id) && !failedOrders.some(failed => failed.id === order.id)
                        ? { ...order, status: newStatus }
                        : order
                );

                setOrders(updatedOrders);
                setSelectedOrders([]);
            }

            // Show appropriate message
            if (failedOrders.length > 0) {
                if (successCount > 0) {
                    setError(t('failedToUpdateOrders', {
                        successCount,
                        failedCount: failedOrders.length
                    }));
                } else {
                    setError(t('failedToUpdateAllOrders', { failedCount: failedOrders.length }));
                }
                // console.error("Failed orders:", failedOrders);
            } else {
                setError("");
                // Display success message
                alert(t('successfullyUpdatedOrders', {
                    count: successCount,
                    status: newStatus
                }));
            }
        } catch (err) {
            console.error("Error in bulk update:", err);
            setError(t('failedToUpdateOrderStatus', { message: err.message }));
        } finally {
            setLoading(false);
        }
    };
    // Render status badge with appropriate style
    const StatusBadge = ({ status }) => {
        const getStatusClass = () => {
            switch (status?.toLowerCase()) {
                case 'delivered':
                    return 'status-delivered';
                case 'processing':
                    return 'status-processing';
                case 'confirmed':
                    return 'status-confirmed';
                case 'pending':
                    return 'status-pending';
                case 'shipped':
                    return 'status-shipped';
                case 'cancelled':
                    return 'status-cancelled';
                case 'refunded':
                    return 'status-refunded';
                default:
                    return '';
            }
        };

        return <span className={`order-status-badge ${getStatusClass()}`}>{status}</span>;
    };

    if (loading && orders.length === 0) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <p>{t('loadingOrders')}</p>
            </div>
        );
    }

    return (
        <div className="admin-orders">
            {/* Page Title */}
            <div className="admin-page-title">
                <h1>{t('orders')}</h1>
                <p>{t('viewAndManageOrders')}</p>
            </div>

            {error && <div className="admin-error-message">{error}</div>}

            {/* Actions Bar */}
            <div className="orders-actions-bar">
                <div className="orders-actions-left">
                    <div className="bulk-actions">
                        <select
                            className="bulk-select"
                            onChange={(e) => handleBulkAction(e.target.value)}
                            value=""
                            disabled={loading}
                        >
                            <option value="" disabled>{t('bulkActions')}</option>
                            <option value="mark-processing">{t('markAsProcessing')}</option>
                            <option value="mark-shipped">{t('markAsShipped')}</option>
                            <option value="mark-delivered">{t('markAsDelivered')}</option>
                        </select>

                        <span className="selected-count">
                            {selectedOrders.length > 0 && `${selectedOrders.length} ${t('selected')}`}
                        </span>
                    </div>
                </div>

                <div className="orders-actions-right">
                    <div className="order-search">
                        <input
                            type="text"
                            placeholder={t('searchOrders')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <select
                        className="order-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="All">{t('allStatus')}</option>
                        <option value="Pending">{t('pending')}</option>
                        <option value="Processing">{t('processing')}</option>
                        <option value="Confirmed">{t('confirmed')}</option>
                        <option value="Shipped">{t('shipped')}</option>
                        <option value="Delivered">{t('delivered')}</option>
                        <option value="Cancelled">{t('cancelled')}</option>
                        <option value="Refunded">{t('refunded')}</option>
                    </select>
                </div>
            </div>

            {/* Orders Table */}
            <div className="admin-table-container orders-table">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                                    disabled={loading}
                                />
                            </th>
                            <th>{t('orderId')}</th>
                            <th>{t('customer')}</th>
                            <th>{t('date')}</th>
                            <th>{t('status')}</th>
                            <th>{t('total')}</th>
                            <th>{t('payment')}</th>
                            <th>{t('actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.length > 0 ? (
                            filteredOrders.map(order => (
                                <tr key={order.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedOrders.includes(order.id)}
                                            onChange={() => handleSelectOrder(order.id)}
                                            disabled={loading}
                                        />
                                    </td>
                                    <td>
                                        {order.orderNumber ? (
                                            <div>
                                                <div className="order-number">#{order.orderNumber}</div>

                                            </div>
                                        ) : (
                                            order.id
                                        )}
                                    </td>
                                    <td className="customer-cell">
                                        <div className="customer-info">
                                            <span className="customer-name">{getCustomerName(order)}</span>
                                            <span className="customer-email">{getCustomerEmail(order)}</span>
                                        </div>
                                    </td>
                                    <td>{formatDate(order.createdAt)}</td>
                                    <td><StatusBadge status={order.status || t('statusPending')} /></td>
                                    <td>{order.currency} {Number(order.total || 0).toFixed(2)}</td>
                                    <td>{typeof order.payment === 'string' ? order.payment : formatPaymentInfo(order.payment)}</td>
                                    <td className="actions-cell">
                                        <button
                                            className="order-action-btn view"
                                            onClick={() => navigate(`/admin/orders/${order.id}`)}
                                            disabled={loading}
                                        >
                                            {t('view')}
                                        </button>
                                        <button
                                            className="order-action-btn invoice"
                                            onClick={() => navigate(`/admin/orders/${order.id}/invoice`)}
                                            disabled={loading}
                                        >
                                            {t('invoice')}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="no-results">
                                    {t('noOrdersFound')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Loading indicator when fetching more */}
            {loading && orders.length > 0 && (
                <div className="loading-more">
                    <div className="loader-small"></div>
                    <p>{t('loadingMoreOrders')}</p>
                </div>
            )}

            {/* Pagination */}
            <div className="pagination">
                {hasMore && (
                    <button
                        className="load-more-btn"
                        onClick={loadMoreOrders}
                        disabled={loading}
                    >
                        {t('loadMoreOrders')}
                    </button>
                )}
            </div>
        </div>
    );
};

export default AdminOrders;